<template>
  <v-container fluid class="pa-6 grey lighten-4">
    <!-- Overlay de carga -->
    <v-overlay :value="cargando" z-index="10">
      <div class="text-center">
        <v-progress-circular indeterminate size="64" color="primary" width="6"></v-progress-circular>
        <div class="mt-4 text-h6 font-weight-light">
          Procesando: {{indiceMasivo}} de {{seleccionados.length}}
        </div>
      </div>
    </v-overlay>

    <!-- Notificaciones (Snackbar) -->
    <v-snackbar v-model="alertaVisible" :color="alertaTipo" top right :timeout="5000">
      {{ alertaMensaje }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="alertaVisible = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

    <!-- Tarjeta de Filtros -->
    <v-card class="mb-6 elevation-2" rounded="lg">
      <v-card-title class="pb-0 grey--text text--darken-2 text-subtitle-1">
        <v-icon left small color="primary">mdi-filter-variant</v-icon>
        PANEL DE CONTROL
      </v-card-title>
      <v-card-text>
        <v-row class="mt-2">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filtroBusqueda"
              label="Buscar en cualquier campo..."
              prepend-inner-icon="mdi-magnify"
              outlined dense hide-details clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="fechaBusqueda"
              type="month"
              label="Filtrar Mes"
              prepend-inner-icon="mdi-calendar-month"
              outlined dense hide-details clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="filtroFirma"
              :items="filtroOpciones"
              label="Estado de Firma"
              prepend-inner-icon="mdi-signature-freehand"
              outlined dense hide-details
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tabla Principal -->
    <v-card class="elevation-2" rounded="lg">
      <v-data-table
        v-model="seleccionados"
        :headers="headers"
        :items="itemsFiltrados"
        show-select
        show-group-by
        item-key="index"
        :sort-by="['fecha']"
        :sort-desc="false"
        :group-by.sync="groupBy"
        @update:group-by="updateGroupBy"
        class="custom-table"
      >
        <!-- Header de Agrupación Dinámico -->
        <template v-slot:group.header="{ items, group, toggle, isOpen, remove }">
          <td :colspan="headers.length + 1" class="grey lighten-5">
            <div class="d-flex align-center">
              <v-btn icon x-small @click="toggle" class="mr-2">
                <v-icon>{{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              </v-btn>
              
              <span class="grey--text text--darken-1 mr-2 text-caption text-uppercase font-weight-bold">
                Agrupado por {{ getHeaderName(newGroupBy) }}:
              </span>
              
              <span class="font-weight-black primary--text">
                {{ formatGroupValue(group) }}
              </span>

              <v-chip x-small class="ml-2" color="primary" outlined>
                {{ items.length }} {{ items.length > 1 ? 'partes' : 'parte' }}
              </v-chip>
              
              <!-- Si se agrupa por Obra, mostrar el nombre del proyecto -->
              <span v-if="newGroupBy && newGroupBy[0] === 'obra'" class="ml-4 grey--text text--darken-2 body-2 italic">
                <v-icon small>mdi-office-building</v-icon>
                {{ items[0].nombre_proyecto }}
              </span>

              <v-spacer></v-spacer>

              <v-btn icon x-small color="red lighten-2" @click="remove">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </td>
        </template>

        <!-- Formato de Columnas -->
        <template v-slot:item.id_partes_de_trabajo="{ item }">
          <v-btn small outlined color="primary" class="font-weight-bold" @click="descargar(item)">
            <v-icon small left>mdi-file-pdf-box</v-icon>
            #{{ item.id_partes_de_trabajo }}
          </v-btn>
        </template>

        <template v-slot:item.fecha="{ item }">
          <span class="text-no-wrap">{{ formatFecha(item.fecha) }}</span>
        </template>

        <template v-slot:item.obra="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip small label outlined v-bind="attrs" v-on="on" color="secondary" class="font-weight-medium">
                {{ item.serie_ot }}{{ item.numero_ot }}
              </v-chip>
            </template>
            <span>{{ item.nombre_proyecto }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.firma="{ item }">
          <v-chip
            small
            :color="item.ruta_firma ? 'success' : 'orange'"
            dark
            class="text-uppercase font-weight-bold"
            style="font-size: 10px; height: 20px;"
          >
            {{ item.firma }}
          </v-chip>
        </template>

        <template v-slot:[`item.str_kms`]="{ item }">
          <span :class="item.str_kms === '-' ? 'grey--text' : ''">{{ item.str_kms }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Footer de Acciones (Solo visible si hay seleccionados) -->
    <v-footer app inset color="transparent" class="pa-0" v-if="seleccionados.length > 0">
      <v-card width="100%" class="pa-4 elevation-10 rounded-t-xl white border-top-primary">
        <v-row align="center">
          <v-col cols="12" sm="6" class="d-flex align-center">
            <v-badge color="primary" :content="seleccionados.length" overlap class="mr-4">
              <v-icon large color="grey darken-1">mdi-file-check</v-icon>
            </v-badge>
            <div>
              <div class="text-subtitle-2 font-weight-bold">Acciones masivas</div>
              <div class="text-caption grey--text">Selecciona la acción para los elementos marcados</div>
            </div>
          </v-col>
          <v-col cols="12" sm="6" class="d-flex justify-end">
            <v-btn color="primary" dark rounded large @click="descargarMasivo" class="mr-2">
              <v-icon left>mdi-download</v-icon> Descargar PDF
            </v-btn>
            <v-btn color="success" dark rounded large @click="downloadExcel">
              <v-icon left>mdi-file-excel</v-icon> Excel
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-footer>

    <!-- Componente de Firma -->
    <v-row v-if="lista.length && puedeFirmar" class="mt-8 mb-16 pb-12">
      <v-col cols="12">
        <v-card outlined class="pa-4 border-dashed rounded-lg">
          <div class="text-h6 mb-4 grey--text text--darken-2">
            <v-icon left color="orange">mdi-pen</v-icon> Firmar partes seleccionados
          </div>
          <FirmaComponent ref="firma" @guardar="guardarFirma" :name="nombre" />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapActions, mapState } from 'vuex';
import FirmaComponent from "@/components/utils/FirmaComponent.vue";
import { PDFDocument } from 'pdf-lib';
import * as XLSX from "xlsx";
import { jwtDecode } from "jwt-decode";

export default {
  components: {
    FirmaComponent,
  },
  data() {
    return {
      groupBy:null,
      newGroupBy:null,
      indiceMasivo: null,

      alertaVisible: false, // Controla la visibilidad de la alerta
      alertaMensaje: "",    // Mensaje de la alerta
      alertaTipo: "success", // Tipo de alerta ('success' o 'error')

      cargando: false,
      groupBy: [],  // Agrupaciones activas
      filtroFirma: 1, // Filtro de firma
      filtroBusqueda: "", // Campo de búsqueda
      fechaBusqueda: "",
      filtroOpciones: [
        { text: 'Sin firma', value: 1 },
        { text: 'Con firma', value: 2 },
        { text: 'Todos', value: 0 },
      ],
      seleccionados: [],
      items: [],
      dataPartes: [],
      headers: [
        { text: 'ID', align: 'start', value: 'id_partes_de_trabajo', groupable: true },
        { text: 'Fecha', align: 'start', value: 'fecha' },
        { text: 'Solicitante', align: 'start', value: 'solicitante_obra', groupable: true },
        { text: 'Trabajador', align: 'start', value: 'trabajador' },
        { text: 'Trabajo Realizado', align: 'start', value: 'trabajo_realizado' },
        { text: 'Horas de Trabajo', align: 'start', value: 'horas_de_trabajo' },
        // { text: 'Responsable', align: 'start', value: 'responsable' },
        { text: 'Obra', align: 'start', value: 'obra' },
        { text: 'Firma', align: 'start', value: 'firma', groupable: true },
        { text: 'Kms', align: 'start', value: 'str_kms', groupable: false },
        { text: 'Dietas', align: 'start', value: 'str_dietas', groupable: true },
        { text: 'Cesta', align: 'start', value: 'str_cesta', groupable: false },
        { text: 'Salida', align: 'start', value: 'str_salida', groupable: false },
      ],
      idCliente: null,
    };
  },
  computed: {
    ...mapState(['permisos']),
    nombre() {
      const ahora = moment().format('DD_MM_YYYY_HH_mm_ss');
      return `${ahora}.png`;
    },
    lista() {
      return this.seleccionados.filter((s)=> s.disabled==false).map(item => item.id_partes_de_trabajo);
    },
    itemsSinFirma() {
      return this.items.filter(it => it.ruta_firma === null);
    },
    itemsConFirma() {
      return this.items.filter(it => it.ruta_firma !== null);
    },
    // Computed para filtrar los items por firma y por búsqueda
    itemsFiltrados() {
      let resultado = this.filtroFirma === 0 ? this.items : (this.filtroFirma === 1 ? this.itemsSinFirma : this.itemsConFirma);

      if (this.fechaBusqueda) {
        resultado = resultado.filter((parte) => parte.fecha.includes(this.fechaBusqueda));
      }

      if (this.filtroBusqueda) {
        resultado = resultado.filter(item => {
          return Object.values(item).some(value =>
            value && value.toString().toLowerCase().includes(this.filtroBusqueda.toLowerCase())
          );
        });
      }

      return resultado;
    },

    puedeFirmar(){
      return this.permisos.includes('firmar-partes');
    },
  },
  methods: {
    ...mapActions(["getData", "postData"]),
    mostrarAlert(){
      this.alertaVisible=true;
       // Desactivar la alerta después de 5 segundos
      setTimeout(() => {
        this.alertaVisible = false;
      }, 5000);  // 5000 ms = 5 segundos
    },

    //funcion para descargar partes en formato PDF
    async descargarMasivo() {
      this.cargando = true;
      this.alertaVisible = false;
      // Crear un nuevo documento PDF donde se combinarán los PDFs
      const pdfDoc = await PDFDocument.create();

      let nombre = "Partes"
      let ok = true
      // Iterar sobre los elementos seleccionados
      const fechaFormateada = moment().format('YYYY_MM_DD_HH_mm_ss');
      nombre=`${nombre}_${fechaFormateada}`

      for (const [index, item] of this.seleccionados.entries()) {
        this.indiceMasivo=index+1;
        try {
          const response = await axios.post(item.obra.includes("K") ? process.env.VUE_APP_RUTA_PDF_KOIOTE : process.env.VUE_APP_RUTA_PDF, 
            { id_parte: item.id_partes_de_trabajo },
            { responseType: 'arraybuffer' }
          );

          // Cargar el PDF descargado
          const pdfBytes = response.data;
          const doc = await PDFDocument.load(pdfBytes);

          // Añadir el PDF al documento combinado
          const [copiedPage] = await pdfDoc.copyPages(doc, doc.getPageIndices());
          pdfDoc.addPage(copiedPage);
         
        } catch (error) {
          ok = false
          console.error("Error al descargar o combinar el PDF:", error);
        }
        
      }
      this.cargando=false;
     

      // Convertir el documento combinado a bytes
      const pdfBytes = await pdfDoc.save();

      // Crear un enlace para descargar el PDF combinado
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }));
      link.setAttribute('download', `${nombre}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      this.cargando = false;
      if(ok){
        this.alertaMensaje = "PDF guardado(s) correctamente.";
        this.alertaTipo = "success";
        this.mostrarAlert();
      } else {
        this.alertaMensaje = "Error al descargar el PDF. Por favor, inténtalo de nuevo.";
        this.alertaTipo = "error";
        this.mostrarAlert();
      }
    },

    //funcion para descargar partes en formato excel
    downloadExcel(){
      if (this.headers.length > 1) {
        const ws = XLSX.utils.aoa_to_sheet([this.headers?.map((header) => header.text)]);
        this.seleccionados.forEach((item) => {
          const row = this.headers?.map((header) => (item[header.value] !== null ? item[header.value] : ""));
          XLSX.utils.sheet_add_aoa(ws, [row], { origin: -1 });
        });
        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(wb, ws, "Data");
        const file_name = `Excel_Partes_${moment().format('YYYY_MM_DD_HH_mm_ss')}`;
        XLSX.writeFile(wb, file_name + ".xlsx");
      }
    },
    async getPartes() {
      const id_cliente = this.idCliente;

      if (!id_cliente) {
        // Redirigir al login
        this.$router
          ? this.$router.push('/login') // Si usas Vue Router
          : (window.location.href = '/login'); // Si no usas Vue Router
        return; // Detener la ejecución del resto del método
      }

      this.dataPartes = await this.getData({ path: `django/portal/trabajos/?id_cliente=${id_cliente}` });
    },

    async guardarFirma(formData) {
      this.cargando = true;
      this.alertaVisible = false; // Oculta la alerta antes de comenzar
      this.lista.forEach(id => {
        formData.append('ids_partes[]', [ 
              id, 
              this.seleccionados.filter((s)=> s.id_partes_de_trabajo==id).map(item => item.obra)[0]
            ]);
      });
      try {
        const resp = await this.postData({
          path: "django/portal/firma/",
          body: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.alertaMensaje = "Firma guardada correctamente.";
        this.alertaTipo = "success";
        this.mostrarAlert();
      } catch (error) {
        this.alertaMensaje = "Error al guardar la firma. Por favor, inténtalo de nuevo.";
        this.alertaTipo = "error";
        this.mostrarAlert();
      } finally {
        this.cargando = false;
        this.cargarDatos();
        this.$refs.firma.clearSignature()
        this.seleccionados=[]
        
      }
      
    },
    isCheckboxDisabled(item) {
      return item.ruta_firma !== null;
    },
    descargar(item) {
      this.cargando = true;
      this.alertaVisible = false;
      // axios.post("https://intranet.rammosindustrial.es/api/partes_de_trabajo/descargar_parte", 
      axios.post(item.obra.includes("K") ? process.env.VUE_APP_RUTA_PDF_KOIOTE : process.env.VUE_APP_RUTA_PDF, 
      {  id_parte: item.id_partes_de_trabajo },
      { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${item.id_partes_de_trabajo}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.cargando = false;
         // Muestra mensaje de éxito
        this.alertaMensaje = "PDF  guardado correctamente.";
        this.alertaTipo = "success";
        this.mostrarAlert();
      }).catch(error => {
        
        this.alertaMensaje = "Error al descargar el PDF. Por favor, inténtalo de nuevo.";
        this.alertaTipo = "error";
        this.mostrarAlert();
        this.cargando = false;

      })
    },
    async cargarDatos() {
      await this.getPartes();
      if (this.dataPartes.length > 0) {
        this.items = this.dataPartes.map((item, index) => ({
          ...item,
          index,
          selected: false,
          disabled: item.ruta_firma != null,
          firma: item.ruta_firma != null ? 'FIRMADO' : 'SIN FIRMA',
          obra: `${item.serie_ot}${item.numero_ot}`,
          str_dietas: item.dietas == 0 ? '-':  item.dieta == 1 ? 'MEDIA DIETA' : 'DIETA COMPLETA',
          str_salida: item.salida ==0 ? '-' : 'SALIDA',
          str_kms: parseFloat(item.kms) == 0? '-': item.kms,
          str_cesta: parseFloat(item.horas_cesta) == 0? '-': item.horas_cesta,
        }));
      }
    },

    //funciones de agrupar
    getHeaderName(groupByArray) {
      if (!groupByArray || groupByArray.length === 0) return '';
      const key = groupByArray[0];
      const header = this.headers.find(h => h.value === key);
      return header ? header.text : key;
    },

    // MEJORADO: Formateo dinámico del valor del grupo
    formatGroupValue(value) {
      if (!value) return "Sin valor";
      
      // Si estamos agrupando por la columna fecha
      if (this.newGroupBy && this.newGroupBy[0] === "fecha") {
        return moment(value).isValid() ? moment(value).format("DD-MM-YYYY") : value;
      }
      
      // Para otros valores, devolver tal cual (o capitalizado)
      return value.toString();
    },

    updateGroupBy(newGroupBy) {
      // Vuetify pasa un array, ej: ["trabajador"]
      this.newGroupBy = newGroupBy;
    },
    // removeGrouping(group) {
    //   this.groupBy = this.groupBy.filter(g => g !== group);
    // },
    // toggleGrouping(group) {
    //   if (this.groupBy.includes(group)) {
    //     // Si ya está agrupado, lo eliminamos
    //     this.groupBy = this.groupBy.filter(g => g !== group);
    //   } else {
    //     // Si no está agrupado, lo agregamos
    //     this.groupBy.push(group);
    //   }
    // },
    //   formatGroup(group) {
    //     // Verifica si el grupo es una fecha (puedes ajustar esta lógica si es necesario)
    //     if (this.newGroupBy && this.newGroupBy[0] === "fecha") {
    //       return moment(group).isValid() ? moment(group).format("DD-MM-YYYY") : group;
    //     }
    //     return group;
    //   },
    //   removeGrouping(group) {
    //     console.log("intentando eliminar el group", group)
    //   // Lógica para eliminar la agrupación especificada
    //   // Si usas Vuetify v-data-table agrupada, puedes restablecer la propiedad que usa para agrupar
    //   this.groupBy = []; // o configura la lógica para manejar otras agrupaciones si es el caso
    // },
    // updateGroupBy(newGroupBy){
    //   this.newGroupBy = newGroupBy
    // },
    formatFecha(fecha) {
      return moment(fecha).format('DD-MM-YYYY');
    }
  },
  created() {
    const validRoles = ["partes", "user", "admin", "developer"];
    if(!validRoles.includes(jwtDecode(localStorage.getItem("token")).rol)){
      this.$router.push(`/${jwtDecode(localStorage.getItem("token")).rol}`);

      return;
    }
    this.idCliente = localStorage.getItem("clienteSeleccionado") ? 
                      localStorage.getItem("clienteSeleccionado") : jwtDecode(localStorage.getItem("token")).id_cliente || 1;
    this.cargarDatos();
  },
};
</script>

<style scoped>
/* Estilos para que las cabeceras se vean como botones de acción */
.custom-table >>> th {
  text-transform: uppercase;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em;
  color: #616161 !important;
  background-color: #fcfcfc !important;
  border-bottom: 2px solid #eee !important;
}

/* Animación y estilo de la barra inferior */
.border-top-primary {
  border-top: 4px solid #1976D2 !important;
}

.border-dashed {
  border: 2px dashed #e0e0e0 !important;
  background-color: #f9f9f9;
}

.custom-table >>> .v-data-table-group__header:hover {
  background-color: #f1f7ff !important;
}

/* Margen inferior para que el contenido no quede oculto tras el footer fix */
.pb-12 {
  padding-bottom: 100px !important;
}
</style>